/**
 * DEV-2809. Метки ссылки, по которой агент пришёл в кабинет (например,
 * `?utm_source=ai_bot` из бота), уходят в регистрацию и становятся источником
 * агента в CRM.
 *
 * Запоминаем их при открытии любой страницы: ссылка может вести на `/`, а гард
 * роутера уводит на вход без query; до формы агент дойдёт позже, иногда в другой
 * вкладке. Хранилище может быть недоступно — тогда метки просто теряются.
 */
import type { AgentRegisterRequest } from '@/features/agent/types/agent.types'

type RegistrationUtm = Pick<AgentRegisterRequest, 'utmSource' | 'utmMedium' | 'utmCampaign'>

const STORAGE_KEY = 'agent-registration-utm'

/** Запомнить метки из адреса; ссылка без utm_source ничего не перезаписывает. */
export function rememberRegistrationUtm(search: string = window.location.search): void {
  const query = new URLSearchParams(search)
  const source = query.get('utm_source')?.trim()
  if (!source) return

  const utm: RegistrationUtm = {
    utmSource: source,
    utmMedium: query.get('utm_medium')?.trim() || undefined,
    utmCampaign: query.get('utm_campaign')?.trim() || undefined,
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(utm))
  } catch {
    // приватный режим или запрет хранилища — источник не узнаем, регистрация не страдает
  }
}

/** Запомненные метки; чужое или битое содержимое хранилища — как будто меток нет. */
export function registrationUtm(): RegistrationUtm {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
    if (!raw || typeof raw !== 'object') return {}

    const pick = (key: keyof RegistrationUtm) => {
      const value = (raw as Record<string, unknown>)[key]
      return typeof value === 'string' && value ? value : undefined
    }
    return {
      utmSource: pick('utmSource'),
      utmMedium: pick('utmMedium'),
      utmCampaign: pick('utmCampaign'),
    }
  } catch {
    return {}
  }
}

/** После регистрации метки больше не нужны: следующий агент с этого устройства — свой источник. */
export function forgetRegistrationUtm(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // нечего чистить
  }
}
